/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { forwardRef, useContext, useState } from 'react';

import type { LearnerProfile_OccupationPreferenceLevel as LearnerProfileOccupationPreferenceLevel } from '__generated__/graphql-types';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, Grid, IconButton, SelectField, SelectOption, Typography2 } from '@coursera/cds-core';
import { AddIcon } from '@coursera/cds-icons';

import { getIndustries } from 'bundles/account-profile/common/constants';
import courseraHiringSolutionsRolesSearchData from 'bundles/account-profile/common/courseraHiringSolutionsRolesSearchData';
import { OccupationRolesContext } from 'bundles/account-profile/components/recruiting-profile-viewer/OccupationRolesProvider';
import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import RequiredFieldLabel from 'bundles/account-profile/components/recruiting-profile-viewer/RequiredFieldLabel';
import { convertLevelEnumToString } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/utils';
import MultiSectionComboBox from 'bundles/employability-common/components/MultiSectionComboBox';
import { searchMatcher } from 'bundles/employability-common/components/utils';

import _t from 'i18n!nls/account-profile';

const styles = {
  selectOption: css`
    width: 100%;
  `,
  headerButton: css`
    margin-left: var(--cds-spacing-200);
    margin-bottom: calc(var(--cds-spacing-150) * -1);
    justify-content: flex-start;
    padding-left: 0;
  `,
  header: css`
    padding-right: var(--cds-spacing-400);
  `,
  levelSelect: css`
    margin-top: var(--cds-spacing-300);

    label {
      margin-bottom: 0;
    }
  `,
  label: css`
    margin-bottom: var(--cds-spacing-150);
  `,
};

const E2E_SELECTORS = {
  INPUT_ROLE: 'profile_edit_role',
} as const;

type Props = {
  availableTalentPipelineRoleOptions: { label: string; value: string }[];
  availableRoleLevels: LearnerProfileOccupationPreferenceLevel[];
  placeholder: string;
  onSelectRole: (value: string) => void;
  onSelectLevel: (value: LearnerProfileOccupationPreferenceLevel) => void;
  onSelectIndustry: (value: string, isKey: boolean) => void;
  onDeleteRole?: () => void;
  shouldRenderLevelsSelect: boolean;
  selectedLevel?: LearnerProfileOccupationPreferenceLevel;
  selectedIndustry?: string;
  errorMessage?: string;
  idx: number;
};

const RolePreferencesSelect = forwardRef<HTMLParagraphElement, Props>(
  (
    {
      availableTalentPipelineRoleOptions,
      availableRoleLevels,
      placeholder,
      onSelectRole,
      onSelectLevel,
      onSelectIndustry,
      onDeleteRole,
      shouldRenderLevelsSelect,
      selectedLevel,
      selectedIndustry,
      errorMessage,
      idx,
    },
    ref
  ) => {
    const { nonActivelyHiringOccupationOptions } = useContext(OccupationRolesContext);
    const { profileState = {} } = useContext(ProfileContext);
    const { displaySettings } = profileState;
    const { enableHiringSolutionsOptInFlow } = displaySettings || {};

    const [roleQuery, setRoleQuery] = useState<string>('');
    const [industryQuery, setIndustryQuery] = useState<string>('');
    const industries = getIndustries();

    const formatRoleComboBoxItem = (label: string, value: string, isTalentPipeline: boolean) => {
      return {
        key: value,
        label,
        onClick: (closePopover?: () => void) => {
          onSelectRole(value);
          closePopover?.();
        },
        match: (term?: string) => {
          const mIdx = courseraHiringSolutionsRolesSearchData.findIndex((role) => role.value === value);
          if (mIdx !== -1) {
            return searchMatcher(
              // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              { label, searchText: courseraHiringSolutionsRolesSearchData?.[mIdx].searchText },
              term
            );
          }
          return searchMatcher({ label }, term);
        },
        content: () => (
          <SelectOption value={value} css={styles.selectOption}>
            {isTalentPipeline ? _t('#{roleName} (Entry-level)', { roleName: label }) : label}
          </SelectOption>
        ),
      };
    };

    const formatIndustryComboBoxItem = (label: string, value: string) => {
      return {
        key: value,
        label,
        onClick: (closePopover?: () => void) => {
          onSelectIndustry(value, true);
          closePopover?.();
        },
        match: (term?: string) => {
          const mIdx = industries.findIndex((ind) => ind.name === value);
          if (mIdx !== -1) {
            // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
            return searchMatcher({ label, searchText: industries?.[mIdx].name }, term);
          }
          return searchMatcher({ label }, term);
        },
        content: () => (
          <SelectOption value={value} css={styles.selectOption}>
            {label}
          </SelectOption>
        ),
      };
    };

    const [comboBoxItems, setComboBoxItems] = useState<Array<{ label: string; value: string }>>(
      nonActivelyHiringOccupationOptions
    );

    const createNewOption = (closePopOver: () => void) => {
      // Check to see if the option already exists
      const existingOption =
        availableTalentPipelineRoleOptions.filter((item) => item.label === roleQuery)?.[0] ||
        comboBoxItems.filter((item) => item.label === roleQuery)?.[0];

      if (!existingOption) {
        setComboBoxItems((prevComboBoxItems) => [...prevComboBoxItems, { value: roleQuery, label: roleQuery }]);
      }

      onSelectRole(existingOption?.value || roleQuery);
      closePopOver();
    };

    const createNewIndustryOption = (closePopOver: () => void) => {
      // Check to see if the option already exists
      const existingOption = industries.filter((item) => item.name === industryQuery)?.[0];

      onSelectIndustry(existingOption?.id || industryQuery, !!existingOption);
      closePopOver();
    };

    return (
      <div data-e2e={E2E_SELECTORS.INPUT_ROLE}>
        <Typography2 variant="subtitleMedium" css={styles.label} component="label">
          {idx === 0 ? (
            <RequiredFieldLabel labelComponent={<span>{_t('What role are you looking for?')}</span>} />
          ) : (
            <span>{_t('What role are you looking for?')}</span>
          )}
        </Typography2>
        <Grid ref={ref} container direction="row" justifyContent="space-between" alignItems="center">
          <MultiSectionComboBox
            errorMessage={errorMessage}
            activePlaceholder={_t('Type to search')}
            items={{
              ...(availableTalentPipelineRoleOptions.length > 0
                ? {
                    [enableHiringSolutionsOptInFlow
                      ? _t('Hiring on Coursera Hiring Solutions')
                      : _t('In-demand roles')]: availableTalentPipelineRoleOptions.map(({ label, value }) =>
                      formatRoleComboBoxItem(label, value, true)
                    ),
                  }
                : {}),
              // We need to format the combo box here, so the most recent version of onSelect is used
              [_t('Other roles')]: comboBoxItems.map(({ label, value }) => formatRoleComboBoxItem(label, value, false)),
            }}
            inactivePlaceholder={placeholder}
            fullWidth={true}
            onSearch={(searchQuery) => setRoleQuery(searchQuery ?? '')}
            header={
              roleQuery
                ? (closePopOver: () => void) => (
                    <div css={styles.header}>
                      <Button
                        css={styles.headerButton}
                        variant="ghost"
                        iconPosition="before"
                        icon={<AddIcon />}
                        onClick={() => createNewOption(closePopOver)}
                        fullWidth
                      >
                        <FormattedMessage message={_t('Create item "{searchText}"')} searchText={roleQuery} />
                      </Button>
                    </div>
                  )
                : undefined
            }
          />
          {onDeleteRole && (
            <IconButton
              variant="ghost"
              intent="delete"
              onClick={onDeleteRole}
              data-testid="delete-role-preference-button"
            />
          )}
        </Grid>
        {shouldRenderLevelsSelect && (
          <SelectField
            placeholder={_t('Select a level')}
            optional
            label={_t('Desired level')}
            fullWidth
            onChange={(e) => onSelectLevel(e.target.value as LearnerProfileOccupationPreferenceLevel)}
            value={selectedLevel ?? ''}
            css={styles.levelSelect}
          >
            {availableRoleLevels.map((level) => (
              <SelectOption value={level} key={level}>
                {convertLevelEnumToString(level)}
              </SelectOption>
            ))}
          </SelectField>
        )}
        <Typography2 variant="subtitleMedium" css={[styles.label, styles.levelSelect]} component="label">
          {_t('Industry')}
        </Typography2>
        <MultiSectionComboBox
          activePlaceholder={_t('Type to search')}
          items={{
            [_t('Industries')]: industries.map((ind) => {
              return formatIndustryComboBoxItem(ind.name, ind.id);
            }),
          }}
          inactivePlaceholder={
            industries.find((ind) => ind.id === selectedIndustry)?.name || selectedIndustry || _t('Select an industry')
          }
          fullWidth={true}
          onSearch={(searchQuery) => setIndustryQuery(searchQuery ?? '')}
          header={
            industryQuery
              ? (closePopOver: () => void) => (
                  <div css={styles.header}>
                    <Button
                      css={styles.headerButton}
                      variant="ghost"
                      iconPosition="before"
                      icon={<AddIcon />}
                      onClick={() => {
                        createNewIndustryOption(closePopOver);
                      }}
                      fullWidth
                    >
                      <FormattedMessage message={_t('Create item "{searchText}"')} searchText={industryQuery} />
                    </Button>
                  </div>
                )
              : undefined
          }
        />
      </div>
    );
  }
);

export default RolePreferencesSelect;
